// What everyone sees about the recording: the countdown and REC badge, how
// their own upload is going, and (for the host) everyone's upload progress.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { RecView } from '../../lib/studio/recording-controller';
import type { UploadStats } from '../../lib/studio/upload-queue';
import { fetchUploads, type TakeUploads } from '../../lib/studio/studio-api';

/** Re-render every `ms` while active (for running clocks). */
export function useTick(active: boolean, ms = 250): void {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [active, ms]);
}

/** 83_000 → "01:23"; an hour or more → "1:02:03". */
export function formatClock(ms: number): string {
  const s = Math.floor(ms / 1000);
  const two = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  return h ? `${h}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}` : `${two(Math.floor(s / 60))}:${two(s % 60)}`;
}

export function RecBanner({ view, countdownMs, elapsedMs }: { view: RecView; countdownMs: () => number; elapsedMs: () => number }) {
  useTick(view.phase !== 'idle');
  return (
    <>
      {view.phase === 'countdown' && (
        <p role="status" className="rounded-md bg-warning-soft px-3 py-2 text-sm font-medium text-warning">
          Recording starts in {Math.max(1, Math.ceil(countdownMs() / 1000))}…
        </p>
      )}
      {view.phase === 'recording' && (
        <p role="status" className="inline-flex items-center gap-2 rounded-md bg-danger px-3 py-1.5 text-sm font-semibold text-white">
          <span aria-hidden className="h-2 w-2 animate-pulse rounded-full bg-white" />
          REC {formatClock(elapsedMs())}
          {view.take && <span className="font-normal opacity-80">· Take {view.take.takeNumber}</span>}
        </p>
      )}
      {view.error && (
        <p role="alert" className="text-sm text-danger">
          {view.error}
        </p>
      )}
    </>
  );
}

const parts = (n: number) => `${n} part${n === 1 ? '' : 's'}`;

/** One line on how this participant's upload is going (null when there's nothing to say). */
export function uploadMessage(stats: UploadStats, recording: boolean): { text: string; tone: 'muted' | 'warning' | 'danger' | 'success' } | null {
  const lost = stats.missing + stats.dropped;
  const lostNote = lost ? ` ${parts(lost)} could not be saved.` : '';
  if (stats.state === 'unauthorized') return { text: 'Uploads stopped: you’re no longer in this studio.', tone: 'danger' };
  if (stats.pending > 0) {
    if (stats.state === 'offline' || stats.state === 'retrying') {
      return { text: `Upload paused — ${parts(stats.pending)} saved on this computer, retrying. Keep this tab open.${lostNote}`, tone: 'warning' };
    }
    return recording
      ? { text: `Uploading as you record (${stats.pending} waiting).`, tone: 'muted' }
      : { text: `Uploading your recording — ${parts(stats.pending)} left. Keep this tab open.${lostNote}`, tone: 'warning' };
  }
  if (recording) return { text: 'Uploading as you record.', tone: 'muted' };
  if (stats.sent > 0) return { text: `Your recording is uploaded.${lostNote}`, tone: lost ? 'warning' : 'success' };
  return null;
}

const TONES = { muted: 'text-muted', warning: 'text-warning', danger: 'text-danger', success: 'text-success' };

export function UploadStatus({ stats, recording }: { stats: UploadStats; recording: boolean }) {
  const msg = uploadMessage(stats, recording);
  if (!msg) return null;
  return (
    <p role="status" aria-live="polite" className={`text-sm ${TONES[msg.tone]}`}>
      {msg.tone === 'success' ? '✓ ' : ''}
      {msg.text}
    </p>
  );
}

/** "audio: 31 parts, 2 to go" for one track. */
export function trackProgress(t: TakeUploads['tracks'][number]): string {
  if (t.status === 'complete' || t.status === 'assembling' || t.status === 'assembled') return `${t.kind} ✓`;
  if (t.pendingChunks === null) return `${t.kind}: ${parts(t.receivedChunks)} in`;
  return `${t.kind}: ${t.pendingChunks} to go`;
}

/** What happens to a take after everyone's uploads are in. */
export const TAKE_STATES: Record<string, { text: string; tone: keyof typeof TONES; done: boolean }> = {
  assembling: { text: 'Putting the recording together…', tone: 'muted', done: false },
  ready: { text: 'Recording ready: everyone’s tracks and a mix are in Media.', tone: 'success', done: true },
  partial: { text: 'Recording ready, with gaps: some parts never arrived. Everyone’s tracks and a mix are in Media.', tone: 'warning', done: true },
  failed: { text: 'No audio was recorded in this take.', tone: 'danger', done: true },
};

/** Host: everyone's uploads for a take, refreshed every few seconds until it's put together. */
export function HostUploads({ sessionId, token, intervalMs = 3000 }: { sessionId: string; token: string; intervalMs?: number }) {
  const q = useQuery({
    queryKey: ['studio-uploads', sessionId],
    queryFn: () => fetchUploads(sessionId, token),
    refetchInterval: (query) => (TAKE_STATES[query.state.data?.session.status ?? '']?.done ? false : intervalMs),
  });
  if (!q.data) return null;
  const people = new Map<string, TakeUploads['tracks']>();
  for (const t of q.data.tracks) people.set(t.displayName, [...(people.get(t.displayName) ?? []), t]);
  return (
    <section aria-label="Uploads" className="max-w-xl rounded-xl border border-border p-4">
      <h2 className="text-sm font-semibold">Take {q.data.session.takeNumber} uploads</h2>
      {people.size === 0 ? (
        <p className="mt-2 text-sm text-muted">Waiting for recordings…</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {[...people].map(([name, tracks]) => (
            <li key={name} className="flex flex-wrap gap-x-3">
              <span className="font-medium">{name}</span>
              <span className="text-muted">{tracks.map(trackProgress).join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
      {TAKE_STATES[q.data.session.status] && (
        <p role="status" className={`mt-3 text-sm ${TONES[TAKE_STATES[q.data.session.status]!.tone]}`}>
          {TAKE_STATES[q.data.session.status]!.text}{' '}
          {q.data.session.status !== 'failed' && TAKE_STATES[q.data.session.status]!.done && (
            <Link to="/media" className="font-medium underline">
              Open Media
            </Link>
          )}
        </p>
      )}
    </section>
  );
}
